import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getAuth, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

// hooks
import { useUpdateAccount } from '../../../../hooks/useUpdateAccount';

// actions
import { setSystemNotification } from '../../../../store/system/system.action';

// components
import ActionField from '../../../../components/action-field/ActionField.js';
import InputBar from '../../../../components/input-bar/InputBar';
import SecondaryBtn from '../../../../components/btns/secondary-btn/SecondaryBtn';

// styles
import { 
  InputContainer,
  NewPasswordInputContainer,
  ChangePasswordContainer,
  PasswordStrengthContainer,
  StrengthBarContainer,
  StrengthBar,
  StrengthTooltip,
  StrengthList,
  StrengthListItem,
  PasswordMatchContainer,
  PasswordMatchText,
  StrengthInfoContainer,
  InfoIcon
} from './ChangePassword.styles.js';

export default function ChangePassword() {
  const dispatch = useDispatch();
  const { updateUserPassword, error, isPending, clearError } = useUpdateAccount();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ strength: '', checks: {} });
  const [isHoveringStrength, setIsHoveringStrength] = useState(false);
  const changePasswordSectionRef = useRef(null);

  const evaluatePasswordStrength = (password) => {
    if (!password) return { strength: '', checks: {} };
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const lengthValid = password.length >= 8;

    const score = [hasUpper, hasLower, hasNumber, hasSpecial, lengthValid].filter(Boolean).length;

    return {
      score,
      strength:
        score === 5 ? 'Strong' :
        score === 4 ? 'Good' :
        score === 3 ? 'Medium' :
        score === 2 ? 'Weak' : 'Very Weak',
      checks: {
        hasUpper,
        hasLower,
        hasNumber,
        hasSpecial,
        lengthValid
      }
    };
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmNewPassword) {
      dispatch(setSystemNotification("Passwords do not match."));
      return;
    }

    if (!currentPassword) {
      dispatch(setSystemNotification("Please enter your current password."));
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;

    if (user && user.email) {
      try {
        // First re-authenticate with current password
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
        
        // Then update password using the hook
        const result = await updateUserPassword(newPassword);
        
        if (result && result.success) {
          dispatch(setSystemNotification("Password updated successfully!"));
          setCurrentPassword('');
          setNewPassword('');
          setConfirmNewPassword('');
          setPasswordStrength({ strength: '', checks: {} });
        } else if (result && result.error) {
          dispatch(setSystemNotification(`Failed to update password: ${result.error}`));
        }
      } catch (error) {
        if (error.code === 'auth/wrong-password') {
          dispatch(setSystemNotification("Current password is incorrect."));
        } else {
          dispatch(setSystemNotification(`Error: ${error.message}`));
        }
      }
    } else {
      dispatch(setSystemNotification("No user is currently signed in."));
    }
  };

  // Clear error when component unmounts
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [error, clearError]);

  // HANDLE NEW PASSWORD BLUR (revert to empty only when leaving the section, not when moving to Confirm/Current)
  const handleNewPasswordBlur = (e) => {
    if (!newPassword) return;
    const nextFocus = e.relatedTarget;
    if (nextFocus && changePasswordSectionRef.current?.contains(nextFocus)) return;
    // relatedTarget can be null in some browsers; defer to check actual focus
    requestAnimationFrame(() => {
      if (document.activeElement && changePasswordSectionRef.current?.contains(document.activeElement)) return;
      setNewPassword('');
      setPasswordStrength({ strength: '', checks: {} });
      setConfirmNewPassword('');
      setCurrentPassword('');
    });
  };

  // HANDLE CONFIRM NEW PASSWORD BLUR (revert to empty only when leaving the section)
  const handleConfirmNewPasswordBlur = (e) => {
    if (!confirmNewPassword) return;
    const nextFocus = e.relatedTarget;
    if (nextFocus && changePasswordSectionRef.current?.contains(nextFocus)) return;
    requestAnimationFrame(() => {
      if (document.activeElement && changePasswordSectionRef.current?.contains(document.activeElement)) return;
      setConfirmNewPassword('');
    });
  };

  // HANDLE CURRENT PASSWORD BLUR (revert to empty only when leaving the section)
  const handleCurrentPasswordBlur = (e) => {
    if (!currentPassword) return;
    const nextFocus = e.relatedTarget;
    if (nextFocus && changePasswordSectionRef.current?.contains(nextFocus)) return;
    requestAnimationFrame(() => {
      if (document.activeElement && changePasswordSectionRef.current?.contains(document.activeElement)) return;
      setCurrentPassword('');
    });
  };

  const passwordsMatch = confirmNewPassword === newPassword && newPassword.length > 0;
  const isPasswordStrong = passwordStrength.score === 5;
  const shouldShowConfirmPassword = isPasswordStrong;
  const shouldShowCurrentPassword = shouldShowConfirmPassword && passwordsMatch;

  return (
    <ChangePasswordContainer ref={changePasswordSectionRef}>
      <ActionField>
        <NewPasswordInputContainer>
          <InputBar
            label={"New Password"}
            type={'password'}
            pH={'Type new password...'}
            value={newPassword}
            updateValue={(val) => {
              setNewPassword(val);
              setPasswordStrength(evaluatePasswordStrength(val));
              setConfirmNewPassword('');
              setCurrentPassword('');
            }}
            onBlur={handleNewPasswordBlur}
            showPasswordToggle
          />
        </NewPasswordInputContainer>
        {newPassword && (
          <PasswordStrengthContainer>
            <StrengthBarContainer>
              <StrengthBar 
                width={(passwordStrength.score / 5) * 100} 
                score={passwordStrength.score}
              />
            </StrengthBarContainer>
            <StrengthInfoContainer>
              <h4>Strength: {passwordStrength.strength}</h4>
              <InfoIcon
                onMouseEnter={() => setIsHoveringStrength(true)}
                onMouseLeave={() => setIsHoveringStrength(false)}
              >
                i
              </InfoIcon>
              {isHoveringStrength && (
                <StrengthTooltip className='panel-bg'>
                  <StrengthList>
                    <StrengthListItem isValid={passwordStrength.checks.lengthValid}>
                      At least 8 characters
                    </StrengthListItem>
                    <StrengthListItem isValid={passwordStrength.checks.hasUpper}>
                      Contains uppercase letter
                    </StrengthListItem>
                    <StrengthListItem isValid={passwordStrength.checks.hasLower}>
                      Contains lowercase letter
                    </StrengthListItem>
                    <StrengthListItem isValid={passwordStrength.checks.hasNumber}>
                      Contains a number
                    </StrengthListItem>
                    <StrengthListItem isValid={passwordStrength.checks.hasSpecial}>
                      Contains special character
                    </StrengthListItem>
                  </StrengthList>
                </StrengthTooltip>
              )}
            </StrengthInfoContainer>
          </PasswordStrengthContainer>
        )}
      </ActionField>
      {shouldShowConfirmPassword && (
        <ActionField>
          <InputContainer>
            <InputBar type={'password'} pH={'Confirm New Password'} value={confirmNewPassword} updateValue={setConfirmNewPassword} onBlur={handleConfirmNewPasswordBlur} showPasswordToggle />
          </InputContainer>
          <PasswordMatchContainer>
            <PasswordMatchText isMatch={passwordsMatch}>
              {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
            </PasswordMatchText>
          </PasswordMatchContainer>
        </ActionField>
      )}
      {shouldShowCurrentPassword && (
        <ActionField>
          <InputContainer>
            <InputBar type={'password'} pH={'Current Password'} value={currentPassword} updateValue={setCurrentPassword} onBlur={handleCurrentPasswordBlur} showPasswordToggle />
          </InputContainer>
          <div onClick={handlePasswordChange}>
            <SecondaryBtn title='UPDATE PASSWORD' isDisabled={isPending} />
          </div>
        </ActionField>
      )}
    </ChangePasswordContainer>
  )
}